import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { getCandidateBySpannerId } from "@/api/candidates";
import { type Candidate } from "@/lib/schema";

export interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: string;
  suggestions?: string[];
  candidateInformation?: CandidateInfo[];
  toolsUsed?: ToolUsage[];
  totalCandidatesFound?: number;
  queryIntent?: string;
  processingTime?: number;
}

export interface CandidateInfo {
  candidateId: string;
  matchScore: number;
  key: string;
  candidate?: Candidate;
  bio?: string;
  // Fields from GraphRAG response (fallback when candidate lookup fails)
  firstName?: string;
  lastName?: string;
  linkedIn?: string;
  resumeUrl?: string;
  matchQuality?: string;
}

export interface ToolUsage {
  tool: string;
  success: boolean;
  candidatesFound?: number;
}

export interface GraphRAGResponse {
  success?: boolean;
  query: string;
  response: string;
  candidateInformation: Array<{
    candidateId: string;
    matchScore: number;
    key: string;
    bio?: string;
    matchQuality?: string;
    scores?: {
      composite: number;
      distance: number;
      experience: number;
      similarity: number;
      skillMatch: number;
    };
  }>;
  toolsUsed: ToolUsage[];
  totalCandidatesFound: number;
  queryIntent: string;
  processingTime: number;
  error?: string;
  timestamp?: number;
}

/**
 * Function to sanitize AI responses by replacing candidate keys with first names (GDPR-compliant)
 */
export const sanitizeResponse = (response: string, candidateKeyMap: { [key: string]: string }): string => {
  let sanitizedResponse = response;
  
  // Replace each candidate key with the corresponding first name
  Object.entries(candidateKeyMap).forEach(([key, firstName]) => {
    // Replace patterns like "candidate_1", "[candidate_1]", "Candidate 1", etc.
    const patterns = [
      new RegExp(`\\b${key}\\b`, 'gi'),           // exact match: candidate_1
      new RegExp(`\\[${key}\\]`, 'gi'),          // bracketed: [candidate_1]
      new RegExp(`\\bCandidate\\s+${key.split('_')[1]}\\b`, 'gi'), // "Candidate 1"
    ];
    
    patterns.forEach(pattern => {
      sanitizedResponse = sanitizedResponse.replace(pattern, firstName);
    });
  });
  
  return sanitizedResponse;
};

/**
 * Fetch candidate information and build key mapping for sanitization
 */
export const fetchCandidatesWithMapping = async (candidateInformation: Array<{
  candidateId: string;
  matchScore: number;
  key: string;
  firstName?: string;
  lastName?: string;
  linkedIn?: string;
  resumeUrl?: string;
  bio?: string;
  matchQuality?: string;
}>): Promise<{
  candidatesWithInfo: CandidateInfo[];
  candidateKeyMap: { [key: string]: string };
}> => {
  const candidatesWithInfo: CandidateInfo[] = [];
  const candidateKeyMap: { [key: string]: string } = {};
    
  for (const candidateInfo of candidateInformation) {
    const candidate = await getCandidateBySpannerId(candidateInfo.candidateId);
    candidatesWithInfo.push({
      ...candidateInfo,
      candidate: candidate || undefined
    });
    
    // Build mapping for response sanitization (GDPR-compliant: only first names)
    // Use candidate from DB first, fallback to response firstName
    if (candidate) {
      candidateKeyMap[candidateInfo.key] = candidate.firstName;
    } else if (candidateInfo.firstName) {
      candidateKeyMap[candidateInfo.key] = candidateInfo.firstName;
    }
  }
  
  return { candidatesWithInfo, candidateKeyMap };
};

/**
 * Call the GraphRAG Firebase function
 */
export const callGraphRAG = async (query: string, maxResults: number = 15): Promise<GraphRAGResponse> => {
  
  // Configure the function with increased timeout (5 minutes)
  const queryGraphRAG = httpsCallable(functions, 'query_graph_rag', {
    timeout: 300000 // 5 minutes in milliseconds
  });
  
  const result = await queryGraphRAG({
    query,
    maxResults
  });
  
  // The new query_graph_rag function returns data nested inside result.data.data
  const responseData = (result.data as any)?.data || result.data;
  
  return responseData as GraphRAGResponse;
};

/**
 * Process GraphRAG response and create AI message
 */
export const processGraphRAGResponse = async (
  responseData: GraphRAGResponse,
  originalQuery: string
): Promise<Message> => {
  // Handle case where responseData might be null/undefined
  if (!responseData) {
    console.error('GraphRAG response is null or undefined');
    throw new Error('No response received from GraphRAG function');
  }
  
  // Check for error field first
  if (responseData.error) {
    console.error('GraphRAG response contains error:', responseData.error);
    throw new Error(responseData.error);
  }
  
  // Since Firebase function returns data directly (not wrapped in data object),
  // we work with responseData directly
  if (!responseData.response) {
    console.error('GraphRAG response missing response field:', responseData);
    throw new Error('GraphRAG response is missing the response text');
  }
  
  // Fetch candidate information and build mapping
  let candidatesWithInfo: CandidateInfo[] = [];
  let sanitizedResponse = responseData.response;
  
  if (responseData.candidateInformation && Array.isArray(responseData.candidateInformation)) {
    const { candidatesWithInfo: candidates, candidateKeyMap } = await fetchCandidatesWithMapping(responseData.candidateInformation);
    candidatesWithInfo = candidates;
    // Sanitize the AI response by replacing candidate keys with first names
    sanitizedResponse = sanitizeResponse(responseData.response, candidateKeyMap);
  }
  
  return {
    id: `ai-${Date.now()}`,
    type: 'ai',
    content: sanitizedResponse || 'I found some candidates for you.',
    timestamp: new Date().toISOString(),
    candidateInformation: candidatesWithInfo,
    toolsUsed: responseData.toolsUsed || [],
    totalCandidatesFound: responseData.totalCandidatesFound || 0,
    queryIntent: responseData.queryIntent || '',
    processingTime: responseData.processingTime || 0
  };
};

/**
 * Generate contextual suggestions based on the query
 */
export const generateSuggestions = (prompt: string): string[] => {
  const lowerPrompt = prompt.toLowerCase();
  
  if (lowerPrompt.includes('python') || lowerPrompt.includes('backend')) {
    return [
      "Find Python developers with Django and PostgreSQL experience",
      "Show me backend engineers with microservices and API design skills",
      "Search for Python developers with machine learning and data science experience"
    ];
  }
  
  if (lowerPrompt.includes('react') || lowerPrompt.includes('frontend')) {
    return [
      "Find React developers with TypeScript and Redux experience",
      "Show me frontend engineers with Next.js and Tailwind CSS skills",
      "Search for React developers with testing experience (Jest, Cypress)"
    ];
  }
  
  if (lowerPrompt.includes('mobile') || lowerPrompt.includes('react native')) {
    return [
      "Find React Native developers with iOS and Android deployment experience",
      "Show me mobile developers with Flutter and native development skills",
      "Search for mobile engineers with app store optimization experience"
    ];
  }
  
  if (lowerPrompt.includes('aws') || lowerPrompt.includes('cloud') || lowerPrompt.includes('devops')) {
    return [
      "Find DevOps engineers with AWS, Docker, and Kubernetes experience",
      "Show me cloud architects with infrastructure as code experience",
      "Search for platform engineers with CI/CD and monitoring skills"
    ];
  }
  
  if (lowerPrompt.includes('full') || lowerPrompt.includes('stack')) {
    return [
      "Find full-stack developers with React and Node.js experience",
      "Show me full-stack engineers with database design skills",
      "Search for full-stack developers with cloud deployment experience"
    ];
  }
  
  return [
    "Find senior software engineers with leadership experience",
    "Show me developers with startup and scale-up experience",
    "Search for engineers with remote work experience",
    "Find candidates with open source contributions"
  ];
};

/**
 * Create the initial welcome message
 */
export const createWelcomeMessage = (): Message => ({
  id: "ai-welcome",
  type: 'ai',
  content: "Hi! I'm **Aria**, your AI recruitment assistant.\n\nI can help you search and analyze candidates from our existing talent pool using natural language queries.\n\n**What I can do:**\n- Find candidates by skills, experience, and location\n- Analyze profiles and provide insights\n- Match candidates to specific roles\n\n**Note:** I only search candidates already in our system.\n\nWhat would you like to know about our candidate pool?",
  timestamp: new Date().toISOString(),
  suggestions: [
    "Find senior React developers with 5+ years experience",
    "Show me Python developers with machine learning skills",
    "Search for full-stack engineers in Cape Town or Johannesburg",
    "Find mobile developers with React Native and iOS experience",
    "Show me DevOps engineers with AWS and Kubernetes skills",
    "Find frontend developers with TypeScript and Next.js experience"
  ]
});

/**
 * Create an error message
 */
export const createErrorMessage = (error: unknown): Message => ({
  id: `ai-${Date.now()}`,
  type: 'ai',
  content: `I apologize, but I encountered an error while processing your request: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
  timestamp: new Date().toISOString()
});

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};
